import { useQuery } from '@tanstack/react-query';

import { authFetch } from '@verantos-dev/core-utils';
import type { ObjectResponse } from '@verantos-dev/types-common';

import { analyticalDatasetKeys } from './queryKeys';
import type { AnalyticalDatasetProfile } from './types';

interface UseAnalyticalDatasetProfileQueryArgs {
  projectId: string;
  analyticalDatasetProfileId: string;
}

// Poll cadence while a run is in flight. Mirrors cohort's 5s profile poll
// (useCohortProfileQuery) — the worker flips latestStatus PROCESSING -> PROCESSED
// /ERROR out of band, and the profile GET is the signal the UI watches.
const PROCESSING_POLL_INTERVAL_MS = 5000;

// Keyed on `profileId` only — same cache entry across versions.
export const useAnalyticalDatasetProfileQuery = ({
  projectId,
  analyticalDatasetProfileId,
}: UseAnalyticalDatasetProfileQueryArgs) => {
  return useQuery({
    queryKey: analyticalDatasetKeys.profile(analyticalDatasetProfileId),
    queryFn: () =>
      authFetch<ObjectResponse<AnalyticalDatasetProfile>>(
        `/analytical-datasets/${encodeURIComponent(projectId)}` +
          `/analytical-dataset-profile/${encodeURIComponent(analyticalDatasetProfileId)}`
      ),
    enabled: projectId.length > 0 && analyticalDatasetProfileId.length > 0,
    retry: false,
    // Auto-poll only while the latest run is PROCESSING; stop on any terminal
    // status. No external `shouldPoll` flag needed — the cached status drives it.
    refetchInterval: (query) =>
      query.state.data?.data.latestStatus === 'PROCESSING' ? PROCESSING_POLL_INTERVAL_MS : false,
    // Recover a stranded PROCESSING status if the tab slept through the worker
    // finishing (matches the cohort profile query's reconnect/focus behavior).
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
  });
};
